import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { authAPI } from '../services/api';
import { getAuthScope, setAuthSession } from '../utils/authStorage';
import { PASSWORD_RULE_HINT, validatePasswordStrength } from '../utils/password';

/** 不可关闭的强制改密弹窗：旧密码 + 新密码 + 确认 */
export default function ForceChangePasswordModal() {
  const { mustChangePassword, setMustChangePassword, setUser, refreshUser } = useAuth();
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  if (!mustChangePassword) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!oldPassword) {
      setError('请填写旧密码');
      return;
    }
    const strengthError = validatePasswordStrength(newPassword);
    if (strengthError) {
      setError(strengthError);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('两次输入的新密码不一致');
      return;
    }
    setLoading(true);
    try {
      const result = await authAPI.changePassword(oldPassword, newPassword);
      // 按角色写回对应作用域，避免路径与角色不一致导致丢 token
      const scope = result.user?.role === 'admin' ? 'admin' : getAuthScope();
      setAuthSession(scope, result.token, result.user, { mustChangePassword: false });
      setUser(result.user);
      setMustChangePassword(false);
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
      await refreshUser();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : '修改密码失败');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/70 flex items-center justify-center p-4"
      // 禁止遮罩关闭 / Esc：不绑定 onClick、不监听 Escape
      role="dialog"
      aria-modal="true"
      aria-labelledby="force-change-password-title"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
        <h2 id="force-change-password-title" className="text-xl font-bold text-gray-900 mb-2">
          需要修改密码
        </h2>
        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4">
          密码强度过低，请修改密码后再使用系统。
        </p>
        <p className="text-xs text-gray-500 mb-4">{PASSWORD_RULE_HINT}</p>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">旧密码</label>
            <input
              type="password"
              value={oldPassword}
              onChange={(e) => setOldPassword(e.target.value)}
              required
              autoFocus
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">新密码</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">确认新密码</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 outline-none"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full py-2.5 rounded-lg bg-gradient-to-r from-teal-500 to-emerald-500 text-white font-medium disabled:opacity-50"
          >
            {loading ? '提交中…' : '确认修改'}
          </button>
        </form>
      </div>
    </div>
  );
}
